import { createAppKit } from '@reown/appkit';
import { bsc, bscTestnet } from '@reown/appkit/networks';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';

const FALLBACK_METADATA = {
  name: 'Matchify Swap',
  description: 'Swap USDT to CDO on Matchify.',
  url: typeof window !== 'undefined' ? window.location.origin : 'https://matchify.ai',
  icons: ['https://matchify.ai/icon.png']
};

function shorten(address) {
  if (!address) return '';
  const addr = address.toLowerCase();
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

class MatchifyWalletController {
  constructor() {
    this.initialized = false;
    this.listeners = new Set();
    this.state = {
      connected: false,
      address: null,
      provider: null,
      web3Provider: null,
      chainId: null
    };
    this.networks = [];
    this.readyPromise = null;
  }

  async init(options = {}) {
    if (this.initialized) {
      return this.readyPromise;
    }

    const projectId = options.projectId || '';
    if (!projectId) {
      console.warn('[MatchifyWallet] projectId missing — wallet connect disabled.');
      this.initialized = true;
      this.readyPromise = Promise.resolve();
      this.notify();
      return this.readyPromise;
    }

    const chainId = Number(options.chainId || 56);
    const networks = this.buildNetworks(chainId);
    this.networks = networks;

    const metadata = {
      ...FALLBACK_METADATA,
      ...(options.metadata || {})
    };

    const adapters = [
      new WagmiAdapter({
        projectId,
        networks
      })
    ];
    const appKit = createAppKit({
      adapters,
      networks,
      metadata,
      projectId,
      features: {
        analytics: true
      },
      excludeWalletIds: options.excludeWalletIds
    });

    this.appKit = appKit;
    this.initialized = true;
    this.readyPromise = (async () => {
      try {
        await appKit.readyPromise;
      } catch (err) {
        console.error('[MatchifyWallet] AppKit init failed', err);
      }
      this.unsubscribeAccount = appKit.subscribeAccount(account => {
        this.updateState({
          connected: Boolean(account?.isConnected),
          address: account?.address || null,
          chainId: account?.chainId || chainId
        });
      }, 'eip155');

      this.unsubscribeNetwork = appKit.subscribeNetwork(network => {
        if (!network) return;
        this.updateState({
          chainId: network?.chainId || network?.caipNetwork?.id || this.state.chainId
        });
      });
      console.info('[MatchifyWallet] AppKit initialised', {
        projectId,
        networkIds: networks.map(n => n?.id).filter(Boolean)
      });
    })();

    return this.readyPromise;
  }

  buildNetworks(chainId) {
    if (Number(chainId) === 97) {
      return [bscTestnet];
    }
    return [bsc];
  }

  updateState(next) {
    const prev = this.state;
    const merged = { ...prev, ...next };
    const provider = this.resolveProvider();
    const web3Provider = this.createWeb3Provider(provider);
    merged.provider = provider || null;
    merged.web3Provider = web3Provider;
    this.state = merged;
    this.notify();
  }

  resolveProvider() {
    if (!this.appKit) {
      return null;
    }
    try {
      const provider = this.appKit.getWalletProvider();
      return provider || null;
    } catch (err) {
      console.error('[MatchifyWallet] Failed to resolve provider', err);
      return null;
    }
  }

  createWeb3Provider(provider) {
    if (!provider) return null;
    const ethersLib = globalThis.ethers;
    if (!ethersLib?.providers?.Web3Provider) return null;
    try {
      return new ethersLib.providers.Web3Provider(provider, 'any');
    } catch (err) {
      console.error('[MatchifyWallet] Failed to create Web3Provider', err);
      return null;
    }
  }

  notify() {
    for (const listener of this.listeners) {
      try {
        listener({ ...this.state });
      } catch (err) {
        console.error('[MatchifyWallet] Listener error', err);
      }
    }
  }

  subscribe(callback) {
    if (typeof callback !== 'function') return () => undefined;
    this.listeners.add(callback);
    callback({ ...this.state });
    return () => this.listeners.delete(callback);
  }

  getState() {
    return { ...this.state };
  }

  async connect() {
    if (!this.initialized) {
      console.warn('[MatchifyWallet] connect called before init');
      return;
    }
    await this.readyPromise;
    try {
      console.info('[MatchifyWallet] opening connect modal');
      await this.appKit.open({ view: 'Connect' });
      console.info('[MatchifyWallet] connect modal invoked');
    } catch (err) {
      console.error('[MatchifyWallet] open modal failed', err);
    }
  }

  async disconnect() {
    if (!this.initialized) return;
    try {
      await this.appKit.disconnect('eip155');
    } catch (err) {
      console.error('[MatchifyWallet] disconnect failed', err);
    } finally {
      this.updateState({
        connected: false,
        address: null,
        provider: null,
        web3Provider: null
      });
    }
  }

  async switchToChain(chainId) {
    if (!this.appKit || !this.networks?.length) return;
    try {
      await this.readyPromise;
      const targetId = Number(chainId);
      const target = this.networks.find(net => Number(net.id) === targetId);
      if (target) {
        await this.appKit.switchNetwork(target);
      }
    } catch (err) {
      console.error('[MatchifyWallet] switch network failed', err);
    }
  }

  getSigner() {
    return this.state.web3Provider ? this.state.web3Provider.getSigner() : null;
  }

  getShortAddress() {
    return shorten(this.state.address);
  }
}

const controller = new MatchifyWalletController();

function bootstrap() {
  if (typeof document === 'undefined') return;
  const host = document.getElementById('swap-root');
  if (!host) return;
  const projectId = host.dataset.wcProjectId || host.dataset.walletProjectId || '';
  const metadata = {
    name: host.dataset.appName || FALLBACK_METADATA.name,
    description: host.dataset.appDescription || FALLBACK_METADATA.description,
    url: host.dataset.appUrl || FALLBACK_METADATA.url,
    icons: host.dataset.appIcons ? host.dataset.appIcons.split(',') : FALLBACK_METADATA.icons
  };
  const chainIdAttr = host.dataset.chainId || host.dataset.chain || '56';
  controller.init({
    projectId,
    metadata,
    chainId: Number(chainIdAttr) || 56
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }
}

globalThis.MatchifyWallet = controller;

export default controller;
